import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CustomCatalogsForVendorSortField,
  SortOrder,
  SuppliersPageCustomCatalogsForVendorDocument,
  useSuppliersPageCustomCatalogsQuery,
  type SuppliersPageCustomCatalogsForVendorQuery,
  type SuppliersPageCustomCatalogsForVendorQueryVariables,
} from '../../../../../generated';
import {
  mapCustomCatalogPage,
  resolveCatalogLookup,
  type CatalogLookup,
  type CustomCatalog,
} from '../utils/customCatalogs';

/**
 * Catalogs per page. Distributors run up to five figures of them, so the list
 * is never loaded whole: the search term narrows it server-side and scrolling
 * asks for the next page.
 */
export const CUSTOM_CATALOG_PAGE_SIZE = 50;

export interface UseCustomCatalogSearchResult {
  catalogs: CustomCatalog[];
  /** The first page is loading; there is nothing to show yet. */
  isLoading: boolean;
  /** A further page is loading under the ones already shown. */
  isLoadingMore: boolean;
  hasMore: boolean;
  errorMessage: string | null;
  loadMore: () => void;
}

interface SearchState {
  /** Vendor + term the rest of this state belongs to. */
  signature: string;
  catalogs: CustomCatalog[];
  cursor: string | null;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  errorMessage: string | null;
}

const emptyState = (signature: string): SearchState => ({
  signature,
  catalogs: [],
  cursor: null,
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
  errorMessage: null,
});

/**
 * A distributor's custom catalogs whose name matches `searchTerm`, a page at a
 * time. Pass an already-debounced term; changing it starts the list again.
 *
 * Catalogs come back sorted by name on the server, so pages append in order.
 * Never re-sort them here: a page cursor only works under the sort it was
 * issued for, and sorting a list that is still growing reshuffles rows the
 * operator is reading.
 */
export const useCustomCatalogSearch = (
  vendorId?: string,
  searchTerm = ''
): UseCustomCatalogSearchResult => {
  const client = useApolloClient();
  const trimmedSearch = searchTerm.trim();
  const signature = `${vendorId ?? ''}::${trimmedSearch}`;

  const [state, setState] = useState<SearchState>(() => emptyState(signature));

  // Read by in-flight requests to notice that the vendor or term changed while
  // they were out, so a stale page is never appended.
  const liveSignature = useRef(signature);

  const loadPage = useCallback(
    async (
      cursor?: string
    ): Promise<SuppliersPageCustomCatalogsForVendorQuery> => {
      const { data } = await client.query<
        SuppliersPageCustomCatalogsForVendorQuery,
        SuppliersPageCustomCatalogsForVendorQueryVariables
      >({
        query: SuppliersPageCustomCatalogsForVendorDocument,
        variables: {
          input: {
            vendorId: vendorId ?? '',
            filter: trimmedSearch ? { search: trimmedSearch } : undefined,
            sort: {
              field: CustomCatalogsForVendorSortField.Name,
              order: SortOrder.Asc,
            },
            pagination: {
              limit: CUSTOM_CATALOG_PAGE_SIZE,
              direction: 'forward',
              cursor,
            },
          },
        },
        // no-cache for the same reason as the product browse: the response is a
        // union whose result member carries no id, so a cache read drops the
        // inline-fragment `edges`.
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      });

      return data;
    },
    [client, vendorId, trimmedSearch]
  );

  // Held in a ref: the loader closes over the Apollo client, and keying the
  // effect on its identity would restart the list whenever that changed.
  const loadPageRef = useRef(loadPage);
  loadPageRef.current = loadPage;

  useEffect(() => {
    liveSignature.current = signature;

    if (!vendorId) {
      setState(emptyState(signature));
      return undefined;
    }

    let isActive = true;
    setState({ ...emptyState(signature), isLoading: true });

    loadPageRef
      .current()
      .then((data) => {
        const page = mapCustomCatalogPage(data);
        if (!isActive) return;

        setState({
          ...emptyState(signature),
          catalogs: page?.kind === 'success' ? page.catalogs : [],
          cursor: page?.kind === 'success' ? page.endCursor : null,
          hasMore: page?.kind === 'success' && page.hasMore,
          errorMessage: page?.kind === 'error' ? page.message : null,
        });
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        setState({
          ...emptyState(signature),
          errorMessage:
            error instanceof Error
              ? error.message
              : 'Failed to load custom catalogs.',
        });
      });

    return () => {
      isActive = false;
    };
  }, [signature, vendorId]);

  // Mirrors the state for `loadMore`, which must read it without taking it as a
  // dependency — a callback identity that changed on every page would detach
  // the scroll listener mid-scroll.
  const stateRef = useRef(state);
  stateRef.current = state;

  const loadMore = useCallback(() => {
    const { current } = stateRef;
    if (
      current.signature !== liveSignature.current ||
      !current.hasMore ||
      !current.cursor ||
      current.isLoading ||
      current.isLoadingMore
    ) {
      return;
    }

    const requestSignature = current.signature;
    // Set eagerly so a burst of scroll events cannot queue the same page twice.
    stateRef.current = { ...current, isLoadingMore: true };
    setState(stateRef.current);

    loadPageRef
      .current(current.cursor)
      .then((data) => {
        const page = mapCustomCatalogPage(data);
        setState((latest) => {
          // The vendor or term changed while this page was out; appending it
          // would poison both the list and the cursor.
          if (latest.signature !== requestSignature) return latest;
          if (page?.kind !== 'success') {
            return { ...latest, isLoadingMore: false };
          }

          return {
            ...latest,
            catalogs: [...latest.catalogs, ...page.catalogs],
            cursor: page.endCursor,
            hasMore: page.hasMore,
            isLoadingMore: false,
          };
        });
      })
      .catch(() => {
        // A failed page leaves the loaded ones intact; scrolling again retries.
        setState((latest) =>
          latest.signature === requestSignature
            ? { ...latest, isLoadingMore: false }
            : latest
        );
      });
  }, []);

  const isCurrent = state.signature === signature;

  return {
    catalogs: isCurrent ? state.catalogs : [],
    isLoading: Boolean(vendorId) && (!isCurrent || state.isLoading),
    isLoadingMore: isCurrent && state.isLoadingMore,
    hasMore: isCurrent && state.hasMore,
    errorMessage: isCurrent ? state.errorMessage : null,
    loadMore,
  };
};

export interface UseCustomCatalogLookupResult {
  /** `null` when there is no catalog id to look up. */
  lookup: CatalogLookup | null;
  retry: () => void;
}

/**
 * A custom catalog looked up by id. Used for the selected catalog, which can sit
 * outside the current search, so the picker can still show what is selected —
 * and tell a deleted catalog apart from a lookup that did not get an answer.
 */
export const useCustomCatalogLookup = (
  id: string | null
): UseCustomCatalogLookupResult => {
  const { data, loading, error, refetch } = useSuppliersPageCustomCatalogsQuery(
    {
      variables: { input: { ids: id ? [id] : [] } },
      skip: !id,
      // Without it a retry keeps showing the failure until the answer lands.
      notifyOnNetworkStatusChange: true,
    }
  );

  const retry = useCallback(() => {
    refetch().catch(() => {
      // The outcome reaches the UI through the hook's own error state.
    });
  }, [refetch]);

  return {
    lookup: id
      ? resolveCatalogLookup(id, { data, loading, hasError: Boolean(error) })
      : null,
    retry,
  };
};
